import { useState, useCallback } from 'react';
import { ToastQueue } from '@adobe/react-spectrum';
import { updateMoment } from 'app/components/RsdForms/api';

interface UseUpdateMomentTypeOptions {
  issueId?: string;
  currentMomentType?: string;
  momentName: string;
  onRefresh: (patchResponse?: unknown) => Promise<void>;
}

interface UpdateMomentTypeParams {
  newMomentType: string;
  onSuccess?: (newType: string) => void;
  onError?: (error: any) => void;
}

/**
 * Custom hook for updating moment type with proper error handling
 *
 * Features:
 * - Makes PATCH API call to update moment type
 * - Shows success toast on successful update
 * - Shows error toast on failure
 *
 * @example
 * const { updateMomentType, isUpdating } = useUpdateMomentType({
 *   issueId: card?.issueId,
 *   currentMomentType: card?.momentType,
 *   momentName,
 *   onRefresh: handleSeamlessDataRefresh
 * });
 *
 * await updateMomentType({ newMomentType: 'New 1.0 Release' });
 */
export const useUpdateMomentType = ({
  issueId,
  currentMomentType,
  momentName,
  onRefresh
}: UseUpdateMomentTypeOptions) => {
  const [isUpdating, setIsUpdating] = useState(false);

  // Helper: Show success state
  const handleSuccess = (
    newType: string,
    onSuccess?: (type: string) => void
  ) => {
    ToastQueue.positive('Your record has been updated.', { timeout: 3000 });
    onSuccess?.(newType);
  };

  // Helper: Handle error state
  const handleError = (error: any, onError?: (err: any) => void) => {
    ToastQueue.negative('Failed to update Moment Type. Please try again.', {
      timeout: 5000
    });

    onError?.(error);
    throw error;
  };

  const updateMomentType = useCallback(
    async ({ newMomentType, onSuccess, onError }: UpdateMomentTypeParams) => {
      // Validation: Check required fields
      if (!issueId) {
        ToastQueue.negative(
          'Failed to update Moment Type. No issue ID available.',
          {
            timeout: 5000
          }
        );
        return;
      }

      if (!currentMomentType) return;

      const previousType = currentMomentType;
      setIsUpdating(true);

      try {
        const patchResponse = await updateMoment(issueId, {
          momentType: newMomentType
        });

        await onRefresh(patchResponse);

        // Show success
        handleSuccess(newMomentType, onSuccess);
      } catch (error) {
        handleError(error, onError);
      } finally {
        setIsUpdating(false);
      }
    },
    [issueId, currentMomentType, momentName, onRefresh]
  );

  return {
    updateMomentType,
    isUpdating
  };
};
